import axios, { CancelToken, isCancel } from 'axios';
import * as Sentry from '@sentry/react';
import { getAccessToken } from '../services/localStorage-api';
import { onRefreshToken, statusCodes } from '../utils';

axios.defaults.retry = 3;

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

let isRefreshing = false;
let failedQueue = [];

const SENTRY_CAPTURE_MARK = '__sentryCaptured';

const sanitizeUrlForSentry = url => {
  if (!url || typeof url !== 'string') {
    return '';
  }

  try {
    const parsed = new URL(url, window.location.origin);

    return parsed.pathname;
  } catch (e) {
    return url.split('?')[0];
  }
};

// --- обработка очереди запросов ---
const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// --- Request interceptor ---
axiosInstance.interceptors.request.use(
  async config => {
    // если явно указано skipAuth, не подставляем токен
    if (config.skipAuth) {
      return config;
    }

    const token = getAccessToken();

    if (token) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      };
    }

    return config;
  },
  error => Promise.reject(error),
);

// --- Response interceptor ---
axiosInstance.interceptors.response.use(
  response => response,

  async error => {
    const originalRequest = error?.config;

    // Отправляем в Sentry все ошибки axios,
    // кроме отменённых запросов.
    if (!isCancel(error) && !error?.[SENTRY_CAPTURE_MARK]) {
      error[SENTRY_CAPTURE_MARK] = true;

      Sentry.withScope(scope => {
        scope.setTag('error.type', 'axios');

        if (originalRequest?.url) {
          scope.setContext('request', {
            method: originalRequest.method?.toUpperCase(),
            url: sanitizeUrlForSentry(originalRequest.url),
          });
        }

        if (error.response) {
          scope.setTag('http.status_code', String(error.response.status));

          scope.setContext('response', {
            status: error.response.status,
            statusText: error.response.statusText,
          });
        } else {
          scope.setTag('error.type', 'network');
        }

        Sentry.captureException(
          new Error(error?.message || 'Axios request failed'),
        );
      });
    }

    // Сетевые ошибки без response
    if (!error.response) {
      return Promise.reject(error);
    }

    const message = error?.message?.toLowerCase() || '';

    if (!originalRequest) {
      return Promise.reject(error);
    }

    if (originalRequest.skipAuth) {
      return Promise.reject(error);
    }

    // --- если 401 и запрос ещё не пробовал refresh ---
    if (
      error?.response?.status === statusCodes.HTTP_401_UNAUTHORIZED &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            if (originalRequest.headers) {
              originalRequest.headers['Authorization'] = `Bearer ${token}`;
            }

            return axiosInstance(originalRequest);
          })
          .catch(err => Promise.reject(err));
      }

      isRefreshing = true;

      try {
        const res = await onRefreshToken();
        const accessToken = res?.data?.access;

        if (accessToken) {
          originalRequest.headers = {
            ...originalRequest.headers,
            Authorization: `Bearer ${accessToken}`,
          };
        }

        processQueue(null, accessToken);

        return axiosInstance(originalRequest);
      } catch (refreshError) {
        if (!refreshError?.[SENTRY_CAPTURE_MARK]) {
          refreshError[SENTRY_CAPTURE_MARK] = true;

          Sentry.withScope(scope => {
            scope.setTag('error.type', 'token_refresh');

            Sentry.captureException(
              new Error(refreshError?.message || 'Token refresh failed'),
            );
          });
        }

        processQueue(refreshError, null);
        window.location.href = '/login';

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // --- retry для timeout / network error ---
    if (message.includes('timeout') || message.includes('network error')) {
      if (typeof originalRequest.retry !== 'number') {
        originalRequest.retry = axios.defaults.retry;
      }

      if (originalRequest.retry <= 0) {
        return Promise.reject(error);
      }

      originalRequest.retry -= 1;

      const delayRetryRequest = new Promise(resolve => {
        setTimeout(() => {
          resolve();
        }, originalRequest.retryDelay || 2000);
      });

      return delayRetryRequest.then(() => axiosInstance(originalRequest));
    }

    return Promise.reject(error);
  },
);

export { CancelToken, isCancel };
export default axiosInstance;
